import React, { useEffect, useState } from "react";
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Table, Typography } from "antd";
import { CloseOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { FORMAT_DATE, LIST_UNITS, requiredItem, TYPE_MODAL } from "../../../constants/constants";
import { filterOption, formatInputNumber, parseInputNumber } from "../../../utils/funcHelper";
import dayjs from "dayjs";
import { consoleLogUtil } from "../../../utils/consoleLogUtil";

const { Title, Text } = Typography;

const ModalImportOrder = ({ visible, onCancel, onSave, warehouses, products, type, data, title }) => {
    const [form] = Form.useForm();
    const [productList, setProductList] = useState([]);
    consoleLogUtil("data in modal", data);
    // Xử lý khi nhấn "Lưu"
    const handleFinish = (values) => {
        const data = {
            ...values,
            import_date: values.import_date ? values.import_date.format(FORMAT_DATE.YYYYMMDD) : null,
            branch_id: warehouses.find(wh => wh.value.toString() === values.warehouse_id)?.branch_id || null,
            items: productList.map((item) => ({
                ...item,
                opening_quantity: item.quantity,
                opening_cost: item.cost_price,
            })),
        };
        if (data.items.length === 0) {
            Modal.error({
                title: "Lỗi",
                content: "Vui lòng thêm ít nhất một sản phẩm vào danh sách.",
            });
            return;
        }
        onSave(data);
    };

    // Thêm sản phẩm vào danh sách
    const handleAddProduct = () => {
        setProductList([
            ...productList,
            {
                key: productList.length + 1,
                product_id: null,
                unit: null,
                quantity: 0,
                cost_price: 0,
                total: 0,
            },
        ]);
    };

    // Xóa sản phẩm khỏi danh sách
    const handleRemoveProduct = (key) => {
        setProductList(productList.filter((item) => item.key !== key));
    };

    // Cập nhật giá trị sản phẩm
    const handleUpdateProduct = (key, field, value, option) => {
        const updatedList = productList.map((item) => {
            if (item.key === key) {
                const updatedItem = { ...item, [field]: value };
                consoleLogUtil("option", option);
                // Tự động gán đơn vị khi chọn sản phẩm
                if (field === "product_id") {
                    const unitId = option?.unit ?? null;
                    const unitName = LIST_UNITS.find((unit) => unit.value === unitId)?.label || "";
                    updatedItem.unit = unitId;
                    updatedItem.unit_name = unitName;
                    updatedItem.cost_price = option?.cost_price ? parseInt(option.cost_price) : 0;
                    updatedItem.total = (updatedItem.quantity || 0) * (updatedItem.cost_price || 0);
                }

                if (field === "quantity" || field === "cost_price") {
                    updatedItem.total = (updatedItem.quantity || 0) * (updatedItem.cost_price || 0);
                }
                
                return updatedItem;
            }
            return item;
        });
        setProductList(updatedList);
    };

    // Tính tổng số lượng và tổng giá trị
    const calculateTotals = () => {
        const totalQuantity = productList.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
        const totalValue = productList.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
        return { totalQuantity, totalValue };
    };

    const { totalQuantity, totalValue } = calculateTotals();

    useEffect(() => {
        if (type === TYPE_MODAL.ADD) {
            form.resetFields();
            setProductList([]);
        } else {
            form.setFieldsValue({
                notes: data?.order?.notes,
                import_date: data?.order?.import_date ? dayjs(data?.order?.import_date, FORMAT_DATE.YYYYMMDD) : null,
            });
            const initialProducts = (data?.items || []).map((item, index) => ({
                key: index + 1,
                product_id: item.product_id,
                unit: products.find(prod => prod.value === item.product_id)?.unit || null,
                unit_name: LIST_UNITS.find(unit => unit.value === products.find(prod => prod.value === item.product_id)?.unit)?.label || "",
                quantity: item.quantity,
                cost_price: item.cost_price,
                total: item.quantity * (item.cost_price ? parseInt(item.cost_price) : 0),
            }));
            setProductList(initialProducts);
        }
    }, [visible, data])

    useEffect(() => {
        if (type === TYPE_MODAL.EDIT && warehouses.length > 0) {
            form.setFieldsValue({
                warehouse_id: data?.order?.warehouse_id ? data?.order?.warehouse_id?.toString() : null
            });
        }
    }, [visible, warehouses])

    return (
        <Modal
            title={title}
            open={visible}
            onCancel={onCancel}
            width={"80%"}
            footer={null}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                {/* Khu vực thông tin chung */}
                <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                    <Title level={5}>Thông tin chung</Title>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                label="Kho"
                                name="warehouse_id"
                                rules={requiredItem}
                            >
                                <Select
                                    placeholder="Chọn kho"
                                    options={warehouses || []}
                                    disabled={type === TYPE_MODAL.EDIT}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Ngày nhập"
                                name="import_date"
                                rules={requiredItem}
                            >
                                <DatePicker
                                    format={FORMAT_DATE.DDMMYYYY}
                                    style={{ width: "100%" }}
                                    disabled={type === TYPE_MODAL.EDIT}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item
                                label="Ghi chú"
                                name="notes"
                            >
                                <Input style={{ height: 32 }} disabled={type === TYPE_MODAL.EDIT} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Khu vực danh sách sản phẩm */}
                <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                    <Title level={5}>Danh sách sản phẩm</Title>
                    <Table
                        dataSource={productList}
                        pagination={false}
                        rowKey="key"
                        bordered
                        columns={[
                            {
                                title: "Tên sản phẩm",
                                dataIndex: "product_id",
                                key: "product_id",
                                width: "40%",
                                render: (value, record) => (
                                    <Select
                                        showSearch
                                        placeholder="Chọn sản phẩm"
                                        value={value}
                                        filterOption={filterOption}
                                        onChange={(val, option) => handleUpdateProduct(record.key, "product_id", val, option)}
                                        options={products || []}
                                        style={{ width: "100%" }}
                                        disabled={type === TYPE_MODAL.EDIT}
                                    />
                                ),
                            },
                            {
                                title: "Đơn vị tính",
                                dataIndex: "unit",
                                key: "unit",
                                align: "center",
                                width: "10%",
                                render: (value, record) => (
                                    <Text>{record?.unit_name}</Text>
                                ),
                            },
                            {
                                title: "Số lượng",
                                dataIndex: "quantity",
                                key: "quantity",
                                align: "center",
                                width: "15%",
                                render: (value, record) => (
                                    <InputNumber
                                        min={0}
                                        value={value}
                                        onChange={(val) => handleUpdateProduct(record.key, "quantity", val)}
                                        style={{ width: "100%" }}
                                        className="input-number-right"
                                        disabled={type === TYPE_MODAL.EDIT}
                                    />
                                ),
                            },
                            {
                                title: "Giá nhập",
                                dataIndex: "cost_price",
                                key: "cost_price",
                                width: "15%",
                                align: "center",
                                render: (value, record) => (
                                    <InputNumber
                                        min={0}
                                        value={value}
                                        formatter={formatInputNumber}
                                        parser={parseInputNumber}
                                        onChange={(val) => handleUpdateProduct(record.key, "cost_price", val)}
                                        style={{ width: "100%" }}
                                        className="input-number-right"
                                        disabled={type === TYPE_MODAL.EDIT}
                                    />
                                ),
                            },
                            {
                                title: "Thành tiền (vnd)",
                                dataIndex: "total",
                                key: "total",
                                width: "15%",
                                align: "center",
                                render: (value) => <div style={{ textAlign: 'end' }}><strong><Text>{formatInputNumber(value)}</Text></strong></div>,
                            },
                            {
                                title: "Hành động",
                                dataIndex: "action",
                                key: "action",
                                align: "center",
                                render: (_, record) => (
                                    type === TYPE_MODAL.EDIT ? null :
                                        <Button
                                            danger
                                            icon={<CloseOutlined />}
                                            onClick={() => handleRemoveProduct(record.key)}
                                            size="small"
                                            style={{ height: 28 }}
                                        />
                                ),
                            },
                        ]}
                    />
                    {
                        type === TYPE_MODAL.EDIT ? null :
                            <Button
                                type="dashed"
                                onClick={handleAddProduct}
                                block
                                icon={<PlusOutlined />}
                                style={{ marginTop: 16 }}
                            >
                                Thêm sản phẩm
                            </Button>
                    }

                </Card>

                {/* Khu vực tổng cộng */}
                <Card size="small" style={{ borderRadius: 8 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Text strong>Tổng số lượng: {totalQuantity}</Text>
                        </Col>
                        <Col span={12} style={{ textAlign: "right" }}>
                            <Text strong>Tổng giá trị: {formatInputNumber(totalValue)} VNĐ</Text>
                        </Col>
                    </Row>
                </Card>

                {/* Nút hành động */}
                <Row justify="end" style={{ marginTop: 16 }}>
                    <Space>
                        <Button onClick={onCancel} icon={<CloseOutlined />}>
                            Đóng
                        </Button>
                        {
                            type === TYPE_MODAL.EDIT ? null :
                                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                    Lưu
                                </Button>
                        }

                    </Space>
                </Row>
            </Form>
        </Modal>
    );
};

export default React.memo(ModalImportOrder);